import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { Award, Briefcase, TrendingUp, Users, BarChart3, Sparkles, IndianRupee } from "lucide-react";
import { KpiTile, SectionCard, DashboardHeader } from "../components/dashboard/shells";

const fmtINR = (v) => {
  const n = Number(v);
  if (!n || Number.isNaN(n)) return "—";
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)} Cr`;
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
  catch { return String(iso).slice(0, 10); }
};

function BranchBars({ rows }) {
  if (!rows.length) return <div className="text-sm text-muted">No course data yet.</div>;
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div className="space-y-3" data-testid="branch-breakdown">
      {rows.map((r) => {
        const placedW = (r.placed / max) * 100;
        const totalW = (r.total / max) * 100;
        return (
          <div key={r.course}>
            <div className="flex justify-between items-baseline text-sm">
              <span className="font-semibold">{r.course}</span>
              <span className="text-xs text-muted">
                <span className="tabular-nums">{r.placed}/{r.total}</span> placed ·
                {" "}<b className="text-signal">{r.placement_pct}%</b>
              </span>
            </div>
            <div className="relative h-2.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-slate-200 rounded-full" style={{ width: `${totalW}%` }} />
              <div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{ width: `${placedW}%`, background: "linear-gradient(90deg, #10B981, #059669)" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CtcHistogram({ buckets }) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <div className="grid grid-cols-5 gap-2 h-40" data-testid="ctc-histogram">
      {buckets.map((b) => {
        const h = Math.max(6, (b.count / max) * 100);
        return (
          <div key={b.label} className="flex flex-col items-center justify-end">
            <div
              className="w-full rounded-t-md flex items-start justify-center text-white text-[11px] font-bold pt-1 transition-all"
              style={{
                height: `${h}%`,
                background: "linear-gradient(180deg, #006CFF 0%, #0040A8 100%)",
              }}
            >
              {b.count > 0 ? b.count : ""}
            </div>
            <div className="text-[10px] text-muted mt-1.5 font-semibold text-center tabular-nums">{b.label}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function InstituteOutcomes() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/institute/outcomes").then((r) => setData(r.data)).catch((e) => setErr(formatError(e)));
  }, []);

  if (err) return <div className="bg-white border border-rule rounded-xl p-5 text-red-600" data-testid="outcomes-error">{err}</div>;
  if (!data) return <div className="text-muted p-4" data-testid="outcomes-loading">Loading placement outcomes…</div>;

  const k = data.kpis || {};
  return (
    <div className="space-y-4" data-testid="institute-outcomes-page">
      <DashboardHeader
        eyebrow="Placement Outcomes"
        title="Cohort outcomes at a glance"
        subtitle="Aggregated across your students currently at offer, hired or onboarded stages."
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <KpiTile icon={Users}       label="Students"       value={k.students_total ?? 0}      accent="#0040A8" testid="kpi-students" />
        <KpiTile icon={Briefcase}   label="Placed"         value={k.placed ?? 0}              accent="#006CFF" testid="kpi-placed" />
        <KpiTile icon={TrendingUp}  label="Placement %"    value={`${k.placement_pct ?? 0}%`} accent="#10B981" testid="kpi-placement-pct" />
        <KpiTile icon={Sparkles}    label="Recruiters"     value={k.recruiters_engaged ?? 0}  accent="#F59E0B" testid="kpi-recruiters" />
        <KpiTile icon={IndianRupee} label="Avg CTC"        value={fmtINR(k.avg_ctc)}          accent="#0EA5E9" testid="kpi-avg-ctc" />
        <KpiTile icon={IndianRupee} label="Median CTC"     value={fmtINR(k.median_ctc)}       accent="#0EA5E9" testid="kpi-median-ctc" />
        <KpiTile icon={Award}       label="Top CTC"        value={fmtINR(k.top_ctc)}          accent="#9933FF" testid="kpi-top-ctc" />
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <SectionCard
          eyebrow="Course-wise placement"
          title="Where placements are coming from"
          className="lg:col-span-3"
          testid="branch-section"
        >
          <BranchBars rows={data.branch_breakdown || []} />
        </SectionCard>
        <SectionCard
          eyebrow="CTC distribution"
          title="Salary spread across placed students"
          className="lg:col-span-2"
          testid="ctc-section"
        >
          <CtcHistogram buckets={data.ctc_histogram || []} />
          <div className="text-[11px] text-muted mt-4">Buckets in ₹ per annum. Postings without a declared salary are excluded.</div>
        </SectionCard>
      </div>

      <SectionCard
        eyebrow="Recently placed students"
        title="Latest offers"
        action={<span className="text-xs text-muted tabular-nums">{(data.recent_placements || []).length} shown</span>}
        testid="recent-placements-section"
        pad={false}
      >
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th>Student</th><th>Course</th><th>Company</th><th>Role</th>
                <th>Status</th><th>CTC</th><th>Date</th>
              </tr>
            </thead>
            <tbody data-testid="recent-placements-tbody">
              {(data.recent_placements || []).map((r, idx) => (
                <tr key={`${r.student_name}-${r.company}-${r.updated_at || idx}`} data-testid={`placement-row-${idx}`}>
                  <td className="font-semibold">{r.student_name}</td>
                  <td>{r.course}</td>
                  <td>{r.company}</td>
                  <td className="text-muted">{r.title || "—"}</td>
                  <td>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${r.status === "hired" || r.status === "onboarded" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="tabular-nums">{fmtINR(r.ctc)}</td>
                  <td className="text-muted text-xs tabular-nums">{fmtDate(r.updated_at)}</td>
                </tr>
              ))}
              {(data.recent_placements || []).length === 0 && (
                <tr><td colSpan={7} className="text-muted text-center py-6">No placements recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
